/******/ (() => { // webpackBootstrap
/* eslint-disable */

/**
 * Format a price in cents using the shop's money_format string.
 * Mirrors the formatMoney helper used elsewhere in the theme's component bundles.
 */
function buildABoxFormatMoney(cents, format) {
  const moneyFormat = format || '${{amount}}';
  const value = (parseInt(cents, 10) || 0) / 100;
  const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
  const match = moneyFormat.match(placeholderRegex);
  if (!match) return `$${value.toFixed(2)}`;

  function withDelimiters(number, precision, thousands, decimal) {
    thousands = thousands || ',';
    decimal = decimal || '.';
    const fixed = number.toFixed(precision);
    const parts = fixed.split('.');
    const dollars = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${thousands}`);
    const centsPart = parts[1] ? decimal + parts[1] : '';
    return dollars + centsPart;
  }

  let formatted;
  switch (match[1]) {
    case 'amount_no_decimals':
      formatted = withDelimiters(value, 0);
      break;
    case 'amount_with_comma_separator':
      formatted = withDelimiters(value, 2, '.', ',');
      break;
    case 'amount_with_space_separator':
      formatted = withDelimiters(value, 2, ' ', ',');
      break;
    case 'amount_no_decimals_with_comma_separator':
      formatted = withDelimiters(value, 0, '.', ',');
      break;
    case 'amount_no_decimals_with_space_separator':
      formatted = withDelimiters(value, 0, ' ');
      break;
    default:
      formatted = withDelimiters(value, 2);
  }

  return moneyFormat.replace(placeholderRegex, formatted);
}

if (!customElements.get('build-a-box')) {
  customElements.define('build-a-box', class BuildABox extends HTMLElement {
    constructor() {
      super();

      this.onTierChange = this.onTierChange.bind(this);
      this.onScentChange = this.onScentChange.bind(this);
      this.onAddToCart = this.onAddToCart.bind(this);
    }

    connectedCallback() {
      window.wetheme.webcomponentRegistry.register({ key: 'component-build-a-box' });
      this.cacheDom();
      this.cacheConfig();
      this.attachEventListeners();
      this.applyTier(this.querySelector('[data-tier-option]:checked'), true);
    }

    cacheDom() {
      this.tierInputs = this.querySelectorAll('[data-tier-option]');
      this.scentInputs = this.querySelectorAll('[data-scent-option]');
      this.pickerWrapper = this.querySelector('[data-picker-wrapper]');
      this.pickerPrompt = this.querySelector('[data-picker-prompt]');
      this.selectedCountDisplay = this.querySelector('[data-selected-count-display]');
      this.qtyWrapper = this.querySelector('[data-quantity-wrapper]');
      this.qtyInput = this.querySelector('[data-quantity-wrapper] .js-qty__num');
      this.summaryEl = this.querySelector('[data-summary]');
      this.subtotalValueEl = this.querySelector('[data-subtotal-value]');
      this.savingsEl = this.querySelector('[data-savings]');
      this.errorEl = this.querySelector('[data-error-message]');
      this.addButton = this.querySelector('[data-add-to-cart-button]');

      this.baseVariantId = this.querySelector('[data-base-variant-id]')?.value;
      this.baseAvailable = this.querySelector('[data-base-variant-available]')?.value === 'true';
      this.baseVariantPrice = parseInt(this.querySelector('[data-base-variant-price]')?.value, 10) || 0;
    }

    cacheConfig() {
      this.sectionId = this.dataset.sectionId;
      this.blockId = this.dataset.blockId;
      this.itemLabelSingular = this.dataset.itemLabelSingular || 'Item';
      this.itemLabelPlural = this.dataset.itemLabelPlural || 'Items';
      this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
      this.selectItemsPromptTemplate = this.dataset.selectItemsPrompt || '';
      this.selectedCountTemplate = this.dataset.selectedCountText || '';
      this.errorSelectionRequiredTemplate = this.dataset.errorSelectionRequired || '';
      this.errorGenericText = this.dataset.errorGeneric || '';
      this.saveTemplate = this.dataset.saveText || '';

      const pageContainer = document.getElementById('PageContainer');
      this.cartType = pageContainer?.dataset.cartType;
      this.cartAction = pageContainer?.dataset.cartAction;
      this.languageUrl = pageContainer?.dataset.languageUrl;
      this.cartCountIndicator = document.querySelector('[data-cart-count-indicator]');

      this.currentTierCount = 0;
      this.currentTierDiscount = 0;
    }

    attachEventListeners() {
      this.tierInputs.forEach((input) => input.addEventListener('change', this.onTierChange));
      this.scentInputs.forEach((input) => input.addEventListener('change', this.onScentChange));
      this.addButton?.addEventListener('click', this.onAddToCart);
    }

    disconnectedCallback() {
      this.tierInputs.forEach((input) => input.removeEventListener('change', this.onTierChange));
      this.scentInputs.forEach((input) => input.removeEventListener('change', this.onScentChange));
      this.addButton?.removeEventListener('click', this.onAddToCart);
    }

    onTierChange(event) {
      this.applyTier(event.currentTarget, false);
    }

    applyTier(input, isInitial) {
      if (!input) return;

      const wasBaseTier = this.currentTierCount === 0;
      this.currentTierCount = parseInt(input.dataset.tierCount, 10) || 0;
      this.currentTierDiscount = parseFloat(input.dataset.tierDiscount) || 0;

      // Clear any previous scent selections when the tier changes
      this.scentInputs.forEach((cb) => {
        cb.checked = false;
        cb.disabled = cb.dataset.unavailable === 'true';
      });

      if (this.currentTierCount === 0) {
        this.pickerWrapper?.classList.add('hidden');
        this.qtyWrapper?.classList.remove('hidden');
        this.summaryEl?.classList.add('hidden');
      } else {
        this.pickerWrapper?.classList.remove('hidden');
        this.qtyWrapper?.classList.add('hidden');
        this.summaryEl?.classList.remove('hidden');

        if (wasBaseTier && !isInitial) {
          this.resetQuantityToOne();
        }
      }

      this.updatePickerPrompt();
      this.updateSelectedCountDisplay();
      this.updateSummary();
      this.clearError();
      this.updateAddButtonState();
    }

    resetQuantityToOne() {
      if (!this.qtyInput) return;
      this.qtyInput.value = 1;
      this.qtyInput.dispatchEvent(new Event('input', { bubbles: true }));
    }

    onScentChange(event) {
      const changedInput = event.currentTarget;
      const checked = Array.from(this.scentInputs).filter((cb) => cb.checked);
      const required = this.currentTierCount;

      if (checked.length > required) {
        changedInput.checked = false;
        this.showError(this.errorSelectionRequiredTemplate
          .replace('__REQUIRED__', required)
          .replace('__LABEL__', this.pluralizedItemLabel(required)));
        return;
      }

      this.clearError();

      const atLimit = checked.length >= required;
      this.scentInputs.forEach((cb) => {
        if (cb.dataset.unavailable === 'true') return;
        if (!cb.checked) cb.disabled = atLimit;
      });

      this.updateSelectedCountDisplay();
      this.updateSummary();
      this.updateAddButtonState();
    }

    pluralizedItemLabel(count) {
      return count === 1 ? this.itemLabelSingular : this.itemLabelPlural;
    }

    updatePickerPrompt() {
      if (!this.pickerPrompt) return;
      this.pickerPrompt.textContent = this.selectItemsPromptTemplate
        .replace('__COUNT__', this.currentTierCount)
        .replace('__LABEL__', this.pluralizedItemLabel(this.currentTierCount));
    }

    updateSelectedCountDisplay() {
      if (!this.selectedCountDisplay) return;
      const selected = Array.from(this.scentInputs).filter((cb) => cb.checked).length;
      this.selectedCountDisplay.textContent = this.selectedCountTemplate
        .replace('__SELECTED__', selected)
        .replace('__REQUIRED__', this.currentTierCount);
    }

    updateSummary() {
      if (this.currentTierCount === 0) return;

      const selectedTotal = Array.from(this.scentInputs)
        .filter((cb) => cb.checked)
        .reduce((sum, cb) => sum + (parseInt(cb.dataset.price, 10) || 0), 0);
      const subtotal = this.baseVariantPrice + selectedTotal;

      if (this.subtotalValueEl) {
        this.subtotalValueEl.textContent = buildABoxFormatMoney(subtotal, this.moneyFormat);
      }

      if (this.savingsEl) {
        if (this.currentTierDiscount > 0) {
          const savings = Math.round(subtotal * (this.currentTierDiscount / 100));
          this.savingsEl.textContent = this.saveTemplate.replace('__AMOUNT__', buildABoxFormatMoney(savings, this.moneyFormat));
          this.savingsEl.classList.remove('hidden');
        } else {
          this.savingsEl.classList.add('hidden');
        }
      }
    }

    updateAddButtonState() {
      if (!this.addButton) return;
      const selected = Array.from(this.scentInputs).filter((cb) => cb.checked).length;
      const isValid = this.currentTierCount === 0
        ? this.baseAvailable
        : this.baseAvailable && selected === this.currentTierCount;

      this.addButton.disabled = !isValid;
      this.addButton.setAttribute('aria-disabled', String(!isValid));
    }

    showError(message) {
      if (!this.errorEl || !message) return;
      this.errorEl.textContent = message;
      this.errorEl.classList.remove('hidden');
    }

    clearError() {
      if (!this.errorEl) return;
      this.errorEl.textContent = '';
      this.errorEl.classList.add('hidden');
    }

    async onAddToCart(event) {
      event.preventDefault();
      this.clearError();

      if (!this.baseVariantId || !this.baseAvailable) return;

      let items;
      const bundleGroup = `${this.blockId}-${Date.now()}`;

      if (this.currentTierCount === 0) {
        const quantity = this.qtyInput ? parseInt(this.qtyInput.value, 10) || 1 : 1;
        items = [{
          id: parseInt(this.baseVariantId, 10),
          quantity,
        }];
      } else {
        const checked = Array.from(this.scentInputs).filter((cb) => cb.checked);
        if (checked.length !== this.currentTierCount) {
          this.showError(this.errorSelectionRequiredTemplate
            .replace('__REQUIRED__', this.currentTierCount)
            .replace('__LABEL__', this.pluralizedItemLabel(this.currentTierCount)));
          return;
        }

        items = [
          {
            id: parseInt(this.baseVariantId, 10),
            quantity: 1,
            properties: { _bundle_tier: String(this.currentTierCount), _bundle_group: bundleGroup },
          },
          ...checked.map((cb) => ({
            id: parseInt(cb.dataset.variantId, 10),
            quantity: 1,
            properties: { _bundle_tier: String(this.currentTierCount), _bundle_group: bundleGroup },
          })),
        ];
      }

      try {
        this.showLoadingState();
        const response = await this.performBulkAdd(items);

        if (response.ok) {
          await this.handleSuccess(response);
        } else if (response.status === 422) {
          const individualResponse = await this.addItemsIndividually(items);
          await this.handleSuccess(individualResponse);
        } else {
          const errorData = await response.json();
          throw new Error(errorData.description || this.errorGenericText);
        }
      } catch (error) {
        console.error('Error adding bundle to cart:', error);
        this.hideLoadingState();
        this.showError(this.errorGenericText);
      }
    }

    async performBulkAdd(items) {
      const payload = { items };
      if (this.cartType === 'drawer') payload.sections = 'cart-drawer';

      return fetch(window.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload),
      });
    }

    async addItemsIndividually(items) {
      let lastSuccessfulResponse = null;
      let successfullyAdded = 0;

      for (const item of items) {
        const payload = { items: [item] };
        if (this.cartType === 'drawer') payload.sections = 'cart-drawer';

        try {
          const response = await fetch(window.routes.cart_add_url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify(payload),
          });

          if (response.ok) {
            successfullyAdded += 1;
            lastSuccessfulResponse = response;
          } else {
            console.warn(`Skipping bundle item ${item.id} due to inventory constraints`);
          }
        } catch (error) {
          console.error(`Error adding bundle item ${item.id}:`, error);
        }
      }

      if (successfullyAdded === 0) {
        throw new Error(this.errorGenericText);
      }

      return lastSuccessfulResponse;
    }

    async handleSuccess(response) {
      this.hideLoadingState();

      if (this.cartType === 'drawer' && response?.ok) {
        try {
          const responseJson = await response.clone().json();
          window.eventBus?.emit('update:cart:drawer', responseJson);
        } catch (error) {
          console.warn('Could not parse response for cart drawer update:', error);
        }
      }

      window.eventBus?.emit('cart:added', { sectionId: this.sectionId });
      this.handleCartAction();
    }

    handleCartAction() {
      if (this.cartType === 'drawer') {
        if (this.cartAction === 'show_added_message') {
          this.showAddedMessage();
          this.updateCartCountIndicator();
        } else if (this.cartAction === 'go_to_or_open_cart') {
          window.eventBus?.emit('open:cart:drawer', { scrollToTop: true });
          this.updateCartCountIndicator();
        }
      } else if (this.cartAction === 'show_added_message') {
        this.showAddedMessage();
        this.updateCartCountIndicator();
      } else {
        window.location = window.routes.cart_url;
      }
    }

    showAddedMessage() {
      const buttonText = this.addButton.querySelector('[data-button-text]');
      const addedText = this.addButton.querySelector('[data-cart-added-text]');
      if (!buttonText || !addedText) return;

      buttonText.style.visibility = 'hidden';
      addedText.style.display = 'block';

      setTimeout(() => {
        addedText.style.display = 'none';
        buttonText.style.visibility = '';
        this.updateAddButtonState();
      }, 2000);
    }

    async updateCartCountIndicator() {
      if (!this.cartCountIndicator) return;

      try {
        const languageParam = !this.languageUrl || this.languageUrl === '/' ? '' : this.languageUrl;
        const response = await fetch(`${languageParam}/cart?view=compare`);
        const cart = await response.json();
        window.wetheme?.updateCartCount?.(cart);
      } catch (error) {
        console.error('Error updating cart count:', error);
      }
    }

    showLoadingState() {
      if (!this.addButton) return;
      this.addButton.disabled = true;

      const buttonText = this.addButton.querySelector('[data-button-text]');
      const loadingIcon = this.addButton.querySelector('[data-loading-icon]');
      if (buttonText) buttonText.style.visibility = 'hidden';
      loadingIcon?.classList.remove('hidden');
    }

    hideLoadingState() {
      if (!this.addButton) return;

      const buttonText = this.addButton.querySelector('[data-button-text]');
      const loadingIcon = this.addButton.querySelector('[data-loading-icon]');
      if (buttonText) buttonText.style.visibility = '';
      loadingIcon?.classList.add('hidden');

      this.updateAddButtonState();
    }
  });
}
/******/ })()
;
